import { SIGNUP_STATUSES_MAP as RetailerSignupStatuses } from '@marketplace/config/retailerSignup.js'
import { SIGNUP_STATUSES_MAP as SupplierSignupStatuses } from '@marketplace/config/supplierSignup.js'

export const CALCULATE_MARGIN = {
    // calculates margin to 1 decimal point and adds 'x' in front
    calculateMargin(msrp, price) {
        const margin = parseFloat((msrp / price).toFixed(1))
        return isFinite(margin) ? `x${margin}` : ``
    },
}

export const DISCOUNTED_PRICE_CALCULATOR = {
    // calculates the discounted price based on percentage
    calculateDiscountedPrice(oldPrice, discountPercentage, rounded = true) {
        if (!discountPercentage) return parseFloat(oldPrice)

        const unfloatPrice = parseFloat(oldPrice) * 100
        if (rounded) {
            return (
                Math.ceil(unfloatPrice * (1 - discountPercentage / 100)) / 100
            )
        }
        return (unfloatPrice * (1 - discountPercentage / 100)) / 100
    },
    calculateDiscountedPriceWithQuantity(
        unitPrice,
        discountPercentage,
        quantity
    ) {
        return unitPrice * quantity * (1 - discountPercentage / 100)
    },
}

export const BEST_BULK_PERCENTAGE = {
    // returns the highest percentage from an Array of bulk discounts
    getBestBulkPercentage(bulkDiscounts) {
        if (!bulkDiscounts || bulkDiscounts.length <= 0) {
            return 0
        }
        return bulkDiscounts.reduce((first, item) => {
            return first < item.percentage ? item.percentage : first
        }, bulkDiscounts[0].percentage)
    },
}

export const BEST_DISCOUNT_OVERALL = {
    // returns the best discount between global and bulk (if quantity is met)
    getBestDiscount(globalPercentage, quantity, bulkDiscounts) {
        let bulkPercentage = 0
        if (bulkDiscounts && bulkDiscounts.length > 0) {
            const activeDiscount = bulkDiscounts.find(
                (item) => quantity >= item.minimumQuantity
            )
            bulkPercentage = activeDiscount ? activeDiscount.percentage : 0
        }
        return Math.max(globalPercentage, bulkPercentage)
    },
}

export const LOWEST_BULK_PRICE_CALCULATOR = {
    // returns the lowest bulk price or nothing
    getLowestBulkPrice(price, bulkDiscounts) {
        if (bulkDiscounts) {
            const lowestBulkDiscount =
                BEST_BULK_PERCENTAGE.getBestBulkPercentage(bulkDiscounts)
            return DISCOUNTED_PRICE_CALCULATOR.calculateDiscountedPrice(
                price,
                lowestBulkDiscount
            )
        } else {
            return -1
        }
    },
}

export const GENERATE_VARIANT_NAME = {
    // changes an array of variant names into a string joined by a hyphen
    // used in  product-variant-selector and product-variant-table
    generateVariantName(variantOptions) {
        if (!variantOptions || variantOptions.length === 0) return ''
        return variantOptions.reduce((acc, variantName) => {
            if (!variantName || !variantName?.value) return acc // Skip null values so that we don't end up showing "XL - null"
            if (!acc) return variantName.value
            return `${acc} - ${variantName.value}`
        }, '')
    },
}

export const CREATE_QUANTITY_SELECT_OPTIONS = {
    // creates a defined list of select options
    // used in case quantity selectbox
    createQuantityOptions(caseQuantity, defaultZero, stockLeft = -1) {
        const optionsAmount = 80
        const maxOptions = stockLeft / caseQuantity
        const quantityArray = []
        for (let index = 1; index <= optionsAmount; index++) {
            const count = index * caseQuantity
            if (stockLeft > 0 && index > maxOptions) {
                break
            }
            quantityArray.push({
                value: index,
                label: `${count} ${
                    count === 1
                        ? this.$t('product-page.unit')
                        : this.$t('product-page.units')
                }`,
            })
        }
        if (defaultZero) {
            quantityArray.unshift({
                value: 0,
                label: `0 ${this.$t('product-page.units')}`,
            })
        }
        return quantityArray
    },
}

export const BLANK_PLACEHOLDER_IMG_URL =
    'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAIBAQEBAQIBAQECAgICAgQDAgICAgUEBAMEBgUGBgYFBgYGBwkIBgcJBwYGCAsICQoKCgoKBggLDAsKDAkKCgr/2wBDAQICAgICAgUDAwUKBwYHCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgr/wgARCAABAAEDAREAAhEBAxEB/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQBAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhADEAAAAHcP/8QAFBABAAAAAAAAAAAAAAAAAAAAAP/aAAgBAQABPwB//8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAgBAgEBPwB//8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAgBAwEBPwB//9k='

export const CONVERT_NUMBER_TO_WORD = {
    // changes 5 to FIVE
    convertNumberToWord(number) {
        const numberWords = ['zero', 'one', 'two', 'three', 'four', 'five']

        return numberWords[number]?.toUpperCase() || 'ONE'
    },
}

export const COMPUTE_LINK_TARGET = {
    computed: {
        linkTarget() {
            return this.$breakpoint.includes('mobile') ? '' : '_blank'
        },
    },
}

export const BEST_LISTING_DISCOUNT = {
    bestListingDiscount(listing) {
        const orderDiscounts = (listing.orderDiscounts?.length > 0 &&
            listing.orderDiscounts) || [{}]
        const discounts = [...orderDiscounts]

        discounts.sort((a, b) => a.minimumOrderValue - b.minimumOrderValue)

        return discounts[0]
    },
}

export const GET_EXPERIMENT_SUB_GROUP = {
    getExperimentSubGroup(payload) {
        const includedInGroup = []
        if (payload <= 50) {
            includedInGroup.push('A')
        }
        if (payload <= 25 || (payload > 50 && payload <= 75)) {
            includedInGroup.push('B')
        }
        if (payload > 25 && payload <= 75) {
            includedInGroup.push('C')
        }
        if (payload > 75) {
            includedInGroup.push('D')
        }

        return includedInGroup.join('')
    },
}

export const CHECK_IF_VIEWER_FINISHED_SIGNUP = (
    signupStatus,
    accountType,
    signupIntent
) => {
    /**
     * Checks which step the user should be redirected to if signup isn't complete
     * if status FINISHED ignore this function
     *
     * @param {object} signupStatus
     * @param {string} accountType
     * @param {string} signupIntent - optional
     * @returns {string}
     */

    if (
        !accountType ||
        !signupStatus ||
        (signupStatus === 'FINISHED' &&
            accountType === 'retailer' &&
            signupIntent) ||
        (signupStatus === 'FINISHED' && accountType === 'supplier')
    )
        return false

    const stepToRedirectTo =
        accountType === 'supplier'
            ? SupplierSignupStatuses[signupStatus]
            : RetailerSignupStatuses[signupStatus]
    if (stepToRedirectTo) {
        return stepToRedirectTo
    }
}
